// Scroll-driven touches for the home page. Everything here is progressive: without IntersectionObserver,
// or when the visitor asks for reduced motion, the page shows its finished state and nothing moves.
import { useEffect, useRef, useState, type RefObject } from "react";

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
}

/**
 * Index of the list child the reader has reached: the last one whose top has crossed the middle of the
 * viewport. Steps run into each other (each one's padding reaches the next), so exactly one holds the
 * eye line at a time. Starts on the last step so the story reads complete if nothing ever fires.
 */
export function useActiveStep<T extends HTMLElement>(count: number): [number, RefObject<T | null>] {
  const ref = useRef<T>(null);
  const [active, setActive] = useState(count - 1);
  useEffect(() => {
    const list = ref.current;
    if (!list || typeof IntersectionObserver === "undefined" || prefersReducedMotion()) return;
    const items = Array.from(list.children) as HTMLElement[];
    const inBand = new Set<number>();
    setActive(0);
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const i = items.indexOf(e.target as HTMLElement);
          if (e.isIntersecting) inBand.add(i);
          else inBand.delete(i);
        }
        if (inBand.size) setActive(Math.max(...inBand));
      },
      // A thin band just above the middle of the viewport, under the sticky card on phones.
      { rootMargin: "-42% 0px -50% 0px" },
    );
    for (const el of items) io.observe(el);
    return () => io.disconnect();
  }, [count]);
  return [active, ref];
}

/**
 * Eases `[data-reveal]` descendants in the first time they scroll into view. Elements already on screen
 * at load are left alone, and the classes come off again afterwards so hover transitions work as before.
 */
export function useReveal<T extends HTMLElement>(): RefObject<T | null> {
  const ref = useRef<T>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === "undefined" || prefersReducedMotion()) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const el = e.target as HTMLElement;
          io.unobserve(el);
          el.classList.add("is-in");
          const done = () => el.classList.remove("reveal", "is-in");
          el.addEventListener("transitionend", done, { once: true });
          setTimeout(done, 1200);
        }
      },
      // The root reaches far above the viewport, so an element a fast scroll jumps over still counts as seen.
      { rootMargin: "100000px 0px -8% 0px" },
    );
    const below = window.innerHeight;
    for (const el of root.querySelectorAll<HTMLElement>("[data-reveal]")) {
      if (el.getBoundingClientRect().top < below) continue;
      el.classList.add("reveal");
      io.observe(el);
    }
    return () => io.disconnect();
  }, []);
  return ref;
}
